import { connect } from 'react-redux'
import { replace } from 'connected-react-router'
import { compose, mapProps, lifecycle, withHandlers, withStateHandlers } from 'recompose'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import withType from 'components/common/hoc/withType'
import withSetBazaarVoiceInlineRatings from 'components/common/hoc/withBazaarVoiceInlineRatings/withSetBazaarVoiceInlineRatings'
import { PROMO_CALL } from 'constant'
import { getPageLocation } from 'selectors'
import { getLocationSearch, getLocationPathname } from 'selectors/getPageLocation'
import { getActiveVehicleId } from 'selectors/getActiveVehicle'
import { getCurrentStoreNumber, getCurrentStorePhoneNumber } from 'selectors/getCurrentStore'
import { getIsStoreLocatorModalInitialized } from 'selectors/storeLocatorModal'
import {
  googleAnalyticProductImpressions,
  googleAnalyticPromotionClicks,
  googleAnalyticZeroTireSearchResults
} from 'actions/googleAnalytic'
import {
  SEARCH_PROMOTION_TYPE,
  RECORDS_INITIAL_COUNT,
  DEFAULT_PROMO_INDEX,
  NORESULT_BY_VEHICLE_SUFFIX,
  NORESULT_BY_SIZE_SUFFIX,
  SWITCH_VEHICLE,
  SWITCH_TIRE_SIZE
} from '../constants'
import { isSearchByVehicleUrl } from 'helpers/location'
import { isAllCompatible, getViewAllQueryParam, getGTMData, getCurrentPageUrl } from '../helpers'
import { getRecords } from './helpers'
import { getUserData } from 'components/Appointment/selectors'

import {
  preLoadItems,
  setSortOptionsData,
  setIsAllCompatible,
  toggleCompareTray,
  setShowNoEligibleTiresForPromo,
  setOeItemIdsData,
  setOeFilterItemsData,
  setOeFilter,
  setVisibleProductsForInventory,
  inventoryDataReset
} from '../actions'
import {
  getCompareTrayShow,
  getCompareTrayItems,
  getPromotionsCartridge,
  getSortOptions,
  getOeFilterMode,
  getOeFilterItems,
  getVisibleProductsForInventory
} from '../selectors'
import TireResultList from './TireResultList'
import { getFavoriteSkus } from 'components/MyFavorites/selectors'

const withViewAllRecords = withStateHandlers(({
  records = [],
  lastRecNum,
  isViewAllResultsQueryParamExists,
  totalNumRecs
}) => ({
  allRecords: records,
  preLoadedRecords: [],
  isViewAllResultsRequest: false,
  viewAllRecords: isViewAllResultsQueryParamExists ||
    records.length <= RECORDS_INITIAL_COUNT || records.length >= totalNumRecs,
  isViewAll: false,
  recordIndex: lastRecNum,
  noNewRecords: false,
  showSpinner: false
}), {
  setViewAllRecords: () => viewAllRecords => ({ viewAllRecords }),
  setIsViewAllResultsRequest: () => isViewAllResultsRequest => ({ isViewAllResultsRequest }),
  setIsViewAll: () => isViewAll => ({ isViewAll }),
  setPreLoaderOptions: () => ({
    isViewAll,
    isViewAllResultsRequest,
    preLoadedSortOptions,
    preLoadedRecords,
    recordIndex
  }) => ({
    isViewAll,
    isViewAllResultsRequest,
    preLoadedSortOptions,
    preLoadedRecords,
    recordIndex
  }),
  setRecordOptions: ({ allRecords, preLoadedRecords }) => () => ({
    allRecords: [...allRecords, ...preLoadedRecords],
    isViewAll: false,
    viewAllRecords: true,
    showSpinner: true
  }),
  setNoNewRecords: () => ({
    noNewRecords,
    preLoadedSortOptions,
    preLoadedRecords
  }) => ({
    noNewRecords,
    preLoadedSortOptions,
    preLoadedRecords
  }),
  resetOptions: () => ({ allRecords, recordIndex }) => ({
    allRecords,
    preLoadedRecords: [],
    isViewAllResultsRequest: false,
    isViewAll: false,
    noNewRecords: false,
    recordIndex,
    showSpinner: false
  })
})

const mapStateToProps = state => {
  const BOTSearchPromotion = getPromotionsCartridge(state)
  const trayOpened = getCompareTrayShow(state)
  const compareTrayItems = getCompareTrayItems(state)
  const search = getLocationSearch(state) || ''
  const hasFilters = !!search.length
  const pathname = getLocationPathname(state)
  const favoriteSkus = getFavoriteSkus(state)
  const oeFilterItems = getOeFilterItems(state)
  const oeFilterMode = getOeFilterMode(state)
  const { isPilotStore } = getUserData(state)

  return {
    BOTSearchPromotion,
    compareTrayItems,
    isViewAllResultsQueryParamExists: Boolean(
      getViewAllQueryParam(getPageLocation(state))
    ),
    currentPath: getCurrentPageUrl(getPageLocation(state)),
    compareBtnText: `${trayOpened ? 'Close ' : ''}Compare`,
    hasFilters,
    favoriteSkus,
    pathname,
    trayItemsCount: compareTrayItems.length,
    modalStoreOptions: getSortOptions(state),
    storeNumber: getCurrentStoreNumber(state),
    storePhoneNumber: getCurrentStorePhoneNumber(state),
    noResultsSuffix: isSearchByVehicleUrl({ pathname }) ? NORESULT_BY_VEHICLE_SUFFIX : NORESULT_BY_SIZE_SUFFIX,
    isStoreLocatorInitialized: getCurrentStoreNumber(state) || getIsStoreLocatorModalInitialized(state),
    tiresLandingPageCTAVerbiage: isSearchByVehicleUrl({ pathname }) ? SWITCH_VEHICLE : SWITCH_TIRE_SIZE,
    activeVehicleId: getActiveVehicleId(state),
    oeFilterMode,
    oeFilterItems,
    visibleProductsForInventory: getVisibleProductsForInventory(state),
    isPilotStore
  }
}

const withInfinityScrollHandlers = withHandlers({
  infinityScrollHandler: ({
    isViewAll,
    recordIndex,
    setRecordOptions,
    preLoadItems,
    setPreLoaderOptions,
    setNoNewRecords
  }) => () => {
    if (isViewAll) {
      setRecordOptions()
      preLoadItems({
        index: recordIndex,
        setNoNewRecords,
        setPreLoaderOptions
      })
    }
  }
})

const withInitializationAndUpdating = lifecycle({
  componentDidMount() {
    const {
      records,
      viewAllRecords,
      sortOptions,
      isStoreLocatorInitialized,
      totalNumRecs,
      googleAnalyticZeroTireSearchResults,
      setShowNoEligibleTiresForPromo,
      withHandleClearFilter,
      hasFilters,
      googleAnalyticProductImpressions,
      favoriteSkus,
      oeItemIdsList,
      setOeItemIdsData,
      oeItems,
      setOeFilterItemsData,
      oeFilterMode,
      setOeFilter,
      setVisibleProductsForInventory,
      isPilotStore
    } = this.props
    const visibleRecords = getRecords({ viewAllRecords, records })

    if (isPilotStore) {
      !isEmpty(visibleRecords) ? setVisibleProductsForInventory(visibleRecords) : setVisibleProductsForInventory([])
    }

    this.props.setIsAllCompatible(isAllCompatible(visibleRecords))
    this.props.setSortOptionsData(sortOptions)
    setShowNoEligibleTiresForPromo(false)

    if (!totalNumRecs) {
      googleAnalyticZeroTireSearchResults()
      if (hasFilters) {
        withHandleClearFilter()
        setShowNoEligibleTiresForPromo(true)
      }
    }

    if (isStoreLocatorInitialized) {
      googleAnalyticProductImpressions({
        data: getGTMData({
          records: visibleRecords,
          favoriteSkus,
          oeItemIdsList
        })
      })
    }

    !isEmpty(oeItemIdsList) && setOeItemIdsData(oeItemIdsList)

    setOeFilterItemsData(oeItems || [])
    setOeFilter(Boolean(oeFilterMode))
  },
  componentDidUpdate(prevProps) {
    const {
      records = [],
      currentPath,
      sortOptions,
      setIsAllCompatible,
      setViewAllRecords,
      isViewAllResultsRequest,
      setIsViewAllResultsRequest,
      setSortOptionsData,
      isStoreLocatorInitialized,
      isViewAllResultsQueryParamExists,
      googleAnalyticProductImpressions,
      resetOptions,
      lastNumRecs,
      storeNumber,
      setShowNoEligibleTiresForPromo,
      activeVehicleId,
      favoriteSkus,
      oeItemIdsList,
      setOeItemIdsData,
      oeFilterMode,
      setOeFilter,
      oeItems,
      setOeFilterItemsData,
      viewAllRecords,
      allRecords,
      visibleProductsForInventory,
      setVisibleProductsForInventory,
      inventoryDataReset,
      isPilotStore
    } = this.props

    const recordsChanged = records !== prevProps.records
    const currentPathChanged = currentPath !== prevProps.currentPath
    const isStoreInitializedChanged = isStoreLocatorInitialized !== prevProps.isStoreLocatorInitialized
    const isStoreChanged = storeNumber !== prevProps.storeNumber
    const visibleRecords = getRecords({ viewAllRecords, records: allRecords })
    const isVisibleRecordsChanged = !isEqual(visibleProductsForInventory, visibleRecords)

    isPilotStore && isVisibleRecordsChanged && setVisibleProductsForInventory(visibleRecords)

    if (activeVehicleId !== prevProps.activeVehicleId) {
      inventoryDataReset()
      setShowNoEligibleTiresForPromo(false)
      !isEmpty(oeItemIdsList) && setOeItemIdsData(oeItemIdsList)
    }

    if (recordsChanged) {
      if (!isViewAllResultsQueryParamExists && !isViewAllResultsRequest) {
        setViewAllRecords(records.length <= RECORDS_INITIAL_COUNT)
      }
      if (!isViewAllResultsRequest || !currentPathChanged) {
        setSortOptionsData(sortOptions)
      }
      resetOptions({ allRecords: records, recordIndex: lastNumRecs })

      setIsAllCompatible(isAllCompatible(records))
    }

    if (isStoreChanged) {
      inventoryDataReset()
      setViewAllRecords(false)
    }

    if (recordsChanged || isViewAllResultsRequest || (isStoreLocatorInitialized && isStoreInitializedChanged)) {
      googleAnalyticProductImpressions({
        data: getGTMData({
          records: getRecords({
            records,
            viewAllRecords: isViewAllResultsQueryParamExists || isViewAllResultsRequest
          }),
          favoriteSkus,
          oeItemIdsList
        })
      })
    }

    if (isViewAllResultsRequest && currentPathChanged) {
      setIsViewAllResultsRequest(false)
    }
    if (!isEqual(oeItems, prevProps.oeItems)) {
      setOeFilterItemsData(oeItems || [])
    }
    setOeFilter(Boolean(oeFilterMode))
  }
})

const withSearchHandlers = withHandlers({
  handlePromotionClick: ({ googleAnalyticPromotionClicks }) => ({ event, item, index }) => {
    if (event.target.getAttribute('data-type') === PROMO_CALL) {
      googleAnalyticPromotionClicks({ item, index, creative: 'tire-search-promo-offers' })
    }
  },
  handleViewAllClick: ({
    recordIndex,
    setRecordOptions,
    preLoadItems,
    setPreLoaderOptions,
    setNoNewRecords
  }) => () => {
    setRecordOptions()
    preLoadItems({
      index: recordIndex,
      setPreLoaderOptions,
      setNoNewRecords
    })
  },
  handleClearFilterClick: ({ pathname, withHandleClearFilter }) => (e) => {
    e.preventDefault()
    pathname && withHandleClearFilter(pathname, { update: true })
  }
})

const withHandleClearFilter = link => (dispatch) => {
  dispatch(replace(link))
}

const withModifiedRecords = mapProps(({
  records: recordsList = [],
  BOTSearchPromotion,
  BOTSearchPromotion: {
    promotions,
    promotionHTML,
    promotionResultList: [promotion = {}, ...promotionResultList] = []} = {},
  gBBDetails,
  ...props
}) => {
  const promoObject = {
    ...(promotionHTML && { promotionHTML }),
    ...((promotions && promotions[0]) || promotion)
  }
  const promoBanner = {
    ...BOTSearchPromotion,
    promotion: promoObject,
    type: SEARCH_PROMOTION_TYPE
  }

  const promoPositionIndex = Number(BOTSearchPromotion.indexInList || DEFAULT_PROMO_INDEX)

  return {
    ...props,
    records: recordsList.length > promoPositionIndex ?
      [
        ...recordsList.slice(0, promoPositionIndex),
        promoBanner,
        ...recordsList.slice(promoPositionIndex)
      ] :
      [...recordsList, promoBanner]
  }
})

const withPartialRecords = mapProps(({
  records,
  allRecords,
  viewAllRecords,
  isViewAllResultsRequest,
  sortOptions,
  modalStoreOptions,
  preLoadedRecords,
  ...props
}) => ({
  ...props,
  viewAllRecords,
  preLoadedRecords,
  isViewAllResultsRequest,
  sortOptions: isViewAllResultsRequest ? modalStoreOptions : sortOptions,
  records: getRecords({ viewAllRecords, records: isViewAllResultsRequest ? allRecords : records })
}))

export default compose(
  withType({ type: 'TireResultList' }),
  connect(mapStateToProps, {
    setSortOptionsData,
    setIsAllCompatible,
    toggleCompareTray,
    googleAnalyticProductImpressions,
    googleAnalyticPromotionClicks,
    googleAnalyticZeroTireSearchResults,
    withHandleClearFilter,
    preLoadItems,
    setShowNoEligibleTiresForPromo,
    setOeItemIdsData,
    setOeFilterItemsData,
    getOeFilterMode,
    getOeFilterItems,
    setOeFilter,
    setVisibleProductsForInventory,
    inventoryDataReset
  }),
  withViewAllRecords,
  withInfinityScrollHandlers,
  withInitializationAndUpdating,
  withSearchHandlers,
  withPartialRecords,
  withSetBazaarVoiceInlineRatings,
  withModifiedRecords
)(TireResultList)
